/* eslint-disable no-nested-ternary */
import React, { useContext, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Img from 'gatsby-image/withIEPolyfill';
import { Link } from 'gatsby';

import ProductPhotos from './ProductPhotos';
import context from '../../context/context';

import Button from '../Button/Button';
import ProductStatus from './ProductStatus';

import { ICommonProduct, IProductInfoProps } from './Types';
import { IProductTypes } from '../CartForm/Types';

import { TRANSLATE } from '../../constants/languages';

import AddToCartSVG from '../../assets/icons/addToCart.svg';
import PlusSVG from '../../assets/icons/plus.svg';
import ArrowSVG from '../../assets/icons/arrow.svg';

import './ProductInfoGood.scss';
import './ProductInfo.scss';
import ModalBig from '../ModalBig/ModalBig';
import Payment from './Payment/Payment';
import Delivery from './Delivery/Delivery';
import Guarantee from './Guarantee/Guarantee';

enum ModalInfo {
  PAYMENT = 'PAYMENT',
  DELIVERY = 'DELIVERY',
  GUARANTEE = 'GUARANTEE',
}

const priceFormat = Intl.NumberFormat('ru', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ProductInfo: React.FC<IProductInfoProps> = ({
  name,
  description,
  price,
  price2,
  price3,
  price4,
  id,
  stock,
  measurment2,
  measurment3,
  measurment4,
  images,
  image,
  specifications,
  brand,
  measurment,
  productAvailable,
  code,
  type,
  link,
  category,
}): JSX.Element => {
  const measureOptions = [measurment, measurment2, measurment3, measurment4].filter(e => e);
  const measurePrice = new Map<string, number>();
  const finalStock = !Number.isNaN(+stock?.replace(',', '.')) ? +stock.replace(',', '.') : 0;

  measurePrice.set(measurment, +price.replace(',', '.'));
  if (!measurePrice.has(measurment2)) measurePrice.set(measurment2, +price2.replace(',', '.'));
  if (!measurePrice.has(measurment3)) measurePrice.set(measurment3, +price3.replace(',', '.'));
  if (!measurePrice.has(measurment4)) measurePrice.set(measurment4, +price4.replace(',', '.'));

  const { language, setProducts } = useContext(context);
  const [isOptionsOpen, setOptionsOpen] = useState<boolean>(false);
  const [modalType, setModalType] = useState(null);
  const [modalStatus, setModalStatus] = useState<boolean>(false);
  const [amount, setAmount] = useState<number>(1);
  const [currentMeasure, setCurrentMeasure] = useState<string>((measureOptions.length && measureOptions[0]) || '');

  const modalContent = () => {
    switch (modalType) {
      case ModalInfo.PAYMENT:
        return <Payment onClose={() => setModalType(null)} />;

      case ModalInfo.DELIVERY:
        return <Delivery onClose={() => setModalType(null)} />;

      case ModalInfo.GUARANTEE:
        return <Guarantee onClose={() => setModalType(null)} />;

      default:
        return null;
    }
  };

  const handleProductAmount = (operation: 'dec' | 'inc') => {
    if (operation === 'inc') return setAmount(prev => prev + 1);
    return setAmount(prev => prev - 1);
  };

  const handleActiveMeasure = (el: string) => {
    setCurrentMeasure(el);
  };

  const handleOptionsOpen = (): void => {
    if (measurment2) {
      setOptionsOpen(prev => !prev);
    }
  };

  const handleAddProduct = ({ oneClickBuy }: { oneClickBuy: boolean }) => {
    const product = {
      link: window.location.pathname,
      name,
      description,
      price,
      price2,
      price3,
      price4,
      measurment2,
      measurment3,
      measurment4,
      stock: finalStock,
      images,
      measurment,
      amount,
      currentMeasure,
      code,
      id,
      category,
      brand,
      oneClickBuy,
    };

    const setDataLayerEvent = () => {
      const cockpitLabel = 'Cockpit__Product__';
      const formattedId = id.replace(cockpitLabel, '').split('_')[0];
      if (typeof window !== 'undefined' && (window as any).dataLayer) {
        (window as any).dataLayer.push({ ecommerce: null });
        (window as any).dataLayer.push({
          event: 'add_to_cart',
          ecommerce: {
            items: [
              {
                item_name: name,
                item_id: formattedId,
                price,
                item_brand: brand,
                item_category: category,
                item_variant: '',
                quantity: amount,
              },
            ],
          },
        });

        console.log({
          item_name: name,
          item_id: formattedId,
          price,
          item_brand: brand,
          item_category: category,
          item_variant: '',
          quantity: amount,
        });
      }
    };

    setDataLayerEvent();

    if (localStorage.getItem('products') === null) {
      setProducts(prev => [...prev, product]);
      setModalStatus(true);
      return localStorage.setItem('products', JSON.stringify([product]));
    }

    const productsCart: IProductTypes[] = JSON.parse(localStorage.getItem('products'));
    if (productsCart.findIndex(el => el.code === code) === -1) {
      productsCart.push(product);
      setProducts(productsCart);
      setModalStatus(true);
      return localStorage.setItem('products', JSON.stringify(productsCart));
    }

    setModalStatus(true);
    setProducts(productsCart.map(item => (item.code === code ? { ...item, oneClickBuy, amount: item.amount + 1 } : item)));

    return localStorage.setItem(
      'products',
      JSON.stringify(productsCart.map(item => (item.code === code ? { ...item, oneClickBuy, amount: item.amount + 1 } : item)))
    );
  };

  const handleOneClickBuy = () => {
    handleAddProduct({ oneClickBuy: true });
    return typeof window !== 'undefined' && window.location.replace('/cart');
  };

  useEffect(() => {
    if (modalStatus) {
      setTimeout(() => setModalStatus(false), 15000);
    }
  }, [modalStatus]);

  const isBrowser = typeof window !== 'undefined';

  useEffect(() => {
    if (isBrowser && (window as any).dataLayer && type === 'product') {
      const cockpitLabel = 'Cockpit__Product__';
      const formattedId = id.replace(cockpitLabel, '').split('_')[0];

      (window as any).dataLayer.push({ ecommerce: null });
      (window as any).dataLayer.push({
        event: 'view_item',
        ecommerce: {
          items: [
            {
              item_name: name,
              item_id: formattedId,
              price,
              item_brand: brand,
              item_category: category,
              item_variant: '',
              quantity: 1,
            },
          ],
        },
      });
    }
  }, [isBrowser]);

  return type === 'product' ? (
    <div className="product-info" id="product-info">
      <div className="product-info-wrapper">
        <ProductPhotos images={images} productStatus={productAvailable} />
        <div className="product-info-rb">
          <h1 className="product-info-title">{name}</h1>
          <div className="product-info-price-wrapper">
            <span className="product-info-price">
              {Array.from(measurePrice)
                .filter(([key, value]) => +value && key === currentMeasure)
                .map(([key, value]) => (
                  <span key={key}>
                    <span>
                      {!Number.isNaN(+value) ? (
                        <>
                          {finalStock !== 0 ? (
                            <span className="product-info-price-stock">
                              <span className="product-info-price-stock-old">{priceFormat.format(value)}&nbsp;грн</span>
                              {priceFormat.format((value / 100) * (100 - finalStock))}
                            </span>
                          ) : (
                            <>{value}</>
                          )}
                        </>
                      ) : (
                        <span className="product-info-price-not-a-number">{priceFormat.format(value)}</span>
                      )}
                      {key && <span className="product-info-measurment"> грн/{key}</span>}
                    </span>
                    <ProductStatus status={productAvailable} />
                  </span>
                ))}
            </span>
          </div>

          <div className="product-info-price-amount">
            <div className="product-info-add">
              {(!productAvailable || productAvailable !== 'OUT_OF_STOCK') && (
                <>
                  <button
                    disabled={amount === 1}
                    type="button"
                    className="product-info-add-dec"
                    onClick={() => handleProductAmount('dec')}
                  />
                  <input
                    className="product-info-add-amount"
                    onChange={e => !Number.isNaN(+e.target.value) && setAmount(+e.target.value)}
                    value={amount}
                  />
                  <button type="button" className="product-info-add-inc" onClick={() => handleProductAmount('inc')}>
                    <PlusSVG />
                  </button>
                </>
              )}
              {productAvailable && !!measureOptions.length && (
                <div className={`product-info-select ${isOptionsOpen ? 'product-info-select--open' : ''}`} onClick={handleOptionsOpen}>
                  <span className="product-info-select-measure">{currentMeasure}</span>
                  {measureOptions.length > 1 && (
                    <>
                      <ArrowSVG />
                      <div className={`product-info-select-dropdown ${isOptionsOpen ? 'product-info-select-dropdown--open' : ''}`}>
                        {measureOptions.map((el, idx) => (
                          <span key={idx} className="product-info-select-dropdown-item" onClick={() => handleActiveMeasure(el)}>
                            {el}
                          </span>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            <span className="product-info-price-amount-total">
              <p>Сума</p>
              <span>{priceFormat.format((measurePrice.get(currentMeasure) / 100) * (100 - finalStock) * amount)} грн</span>
            </span>
          </div>

          {productAvailable === 'IN_STOCK' || productAvailable === null ? (
            <div className={`product-info-buttons ${productAvailable === 'OUT_OF_STOCK' && 'unavilable'}`}>
              <Button htmlType="button" color="blue" type="product" click={() => handleAddProduct({ oneClickBuy: false })}>
                {TRANSLATE[language as 'ru' | 'ua'].addToCart}
              </Button>
              <div className="product-info-buttons-add" onClick={() => handleAddProduct({ oneClickBuy: false })}>
                <AddToCartSVG />
              </div>

              <Button htmlType="button" type="product" click={() => handleOneClickBuy()}>
                {TRANSLATE[language as 'ru' | 'ua'].oneClickBuy}
              </Button>
            </div>
          ) : (
            <h2>
              {productAvailable === 'UNAVAILABLE'
                ? TRANSLATE[language as 'ru' | 'ua'].unavailable
                : productAvailable === 'UNDER_THE_ORDER'
                ? TRANSLATE[language as 'ru' | 'ua'].underOrder
                : productAvailable === 'EXPECTING'
                ? TRANSLATE[language as 'ru' | 'ua'].expecting
                : ''}
            </h2>
          )}
          <div className="product-info-links">
            <button type="button" onClick={() => setModalType(ModalInfo.PAYMENT)}>
              Оплата
            </button>
            <button type="button" onClick={() => setModalType(ModalInfo.DELIVERY)}>
              Доставка
            </button>
            <button type="button" onClick={() => setModalType(ModalInfo.GUARANTEE)}>
              Гарантія
            </button>
          </div>
          <br />
        </div>
        {modalType && createPortal(<ModalBig close={() => setModalType(null)}>{modalContent()}</ModalBig>, document.body)}
        {modalStatus &&
          createPortal(
            <div className="product-info-cart-modal">
              <h2>{TRANSLATE[language as 'ru' | 'ua'].productAdded}</h2>
              <Button
                htmlType="button"
                type="primary"
                color="yellow"
                click={() => typeof window !== 'undefined' && window.location.replace('/cart')}
              >
                Відкрити
              </Button>
            </div>,
            document.body
          )}
      </div>
      <div className="blue-circle" />
      <div className="yellow-circle" />
    </div>
  ) : (
    <div className="product-info-good">
      <Link to={link}>
        <Img fluid={image?.childImageSharp?.fluid} objectFit="contain" className="product-info-good__img" />
      </Link>
      <div className="product-info-good__text-container">
        <Link to={link}>
          <h2 className="product-info-good__title">{name}</h2>
        </Link>

        {(specifications || brand) && (
          <div className="product-info-good__specifications">
            {brand && <div className="product-info-good__specifications-item">Бренд: {brand}</div>}

            {specifications &&
              Object.keys(specifications)
                .slice(0, 4)
                .map(key => (
                  <div key={key} className="product-info-good__specifications-item">
                    {`${key}: ${specifications[key]}`}
                  </div>
                ))}
          </div>
        )}
        <div className="product-info-good__buy">
          <div className="product-info-good__price">
            <div className="product-info-good__price-group">
              <span className="product-info-good__price-item">
                {Array.from(measurePrice)
                  .filter(([key, value]) => +value && key === currentMeasure)
                  .map(([key, value]) => (
                    <span key={key}>
                      {!Number.isNaN(+value) ? (
                        <>
                          {finalStock !== 0 ? (
                            <span className="product-info-price-stock">
                              <span className="product-info-price-stock-old">{value}</span>
                              {priceFormat.format((value / 100) * (100 - finalStock))} грн
                            </span>
                          ) : (
                            <>{priceFormat.format(value * amount)} грн</>
                          )}
                        </>
                      ) : (
                        <span className="product-info-price-not-a-number">{value}</span>
                      )}
                    </span>
                  ))}
              </span>
              {productAvailable && !!measureOptions.length && (
                <div
                  className={`product-info-good-select ${isOptionsOpen ? 'product-info-good-select--open' : ''}`}
                  onClick={handleOptionsOpen}
                >
                  <span className="product-info-good-select-measure">{currentMeasure}</span>
                  {measureOptions.length > 1 && (
                    <>
                      <ArrowSVG />
                      <div
                        className={`product-info-good-select-dropdown ${isOptionsOpen ? 'product-info-good-select-dropdown--open' : ''}`}
                      >
                        {measureOptions.map((el, idx) => (
                          <span key={idx} className="product-info-good-select-dropdown-item" onClick={() => handleActiveMeasure(el)}>
                            {el}
                          </span>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="product-info-good__amount">
              <div className="product-info-good-add">
                {(!productAvailable || productAvailable !== 'OUT_OF_STOCK') && (
                  <>
                    <button
                      disabled={amount === 1}
                      type="button"
                      className="product-info-good-add-dec"
                      onClick={() => handleProductAmount('dec')}
                    />
                    <input
                      className="product-info-good-add-amount"
                      onChange={e => !Number.isNaN(+e.target.value) && setAmount(+e.target.value)}
                      value={amount}
                    />
                    <button type="button" className="product-info-good-add-inc" onClick={() => handleProductAmount('inc')}>
                      <PlusSVG />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className={`product-info-good__buttons ${productAvailable === 'OUT_OF_STOCK' ? 'unavailable' : ''}`}>
            <div onClick={() => handleAddProduct({ oneClickBuy: false })}>
              <AddToCartSVG />
            </div>
            <Button htmlType="button" type="product" click={() => handleOneClickBuy()}>
              {TRANSLATE[language as 'ru' | 'ua'].oneClickBuy}
            </Button>
          </div>
        </div>
      </div>
      <div className="product-info-good__status">
        <ProductStatus status={productAvailable} />
      </div>
      {finalStock !== 0 && (
        <div className="product-info-good__status product-info-good__status_discount">
          <h2>Знижка</h2>
        </div>
      )}
      {modalStatus &&
        createPortal(
          <div className="product-info-cart-modal">
            <h2>{TRANSLATE[language as 'ru' | 'ua'].productAdded}</h2>
            <Button
              htmlType="button"
              type="primary"
              color="yellow"
              click={() => typeof window !== 'undefined' && window.location.replace('/cart')}
            >
              Відкрити
            </Button>
          </div>,
          document.body
        )}
    </div>
  );
};

export default ProductInfo;
